import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

/**
 * Recoup Statement — fuel drawn from a nozzle and returned to the tank (testing, density).
 * Shows totals by product and by employee, then every entry. Filters are optional.
 */
const litres = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const csvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const RecoupStatementReport = (props) => {
  const [meta, setMeta] = useState({ products: [], employees: [], tanks: [] });
  const [inputValues, setInputValues] = useState({ product: [], employee: [], tank: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/recoup_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Recoup Statement', error.message, 'error'));
  }, []);

  const toggleSelection = (field, id) => {
    const selected = new Set((inputValues[field] || []).map(String));
    const key = String(id);
    if (selected.has(key)) selected.delete(key);
    else selected.add(key);
    setInputValues({ ...inputValues, [field]: Array.from(selected) });
  };

  const setSelection = (field, ids) => setInputValues({ ...inputValues, [field]: (ids || []).map(String) });

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    product: inputValues.product,
    employee: inputValues.employee,
    tank: inputValues.tank,
  });

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) return;
    setLoading(true);
    setReport(null);
    try {
      setReport(await fetchReportJson(`${API_URL}/recoup_statement`, buildPayload()));
    } catch (error) {
      swal('Recoup Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) return;
    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/recoup_statement/pdf`,
        buildPayload(),
        `recoup_statement_${inputValues.start_date}_${inputValues.end_date}.pdf`,
      );
    } catch (error) {
      swal('Recoup Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!report) return;
    const lines = [
      [report.pump_name, 'Recoup Statement', `${report.start_date} to ${report.end_date}`],
      [],
      ['Date', 'Employee', 'Shift start', 'Product', 'Tank', 'Litres', 'Price per litre', 'Sale reversed', 'Cost per litre', 'Cost value', 'Note', 'Check'],
      ...(report.rows || []).map((r) => [
        r.date, r.employee, r.shift_start, r.product, r.tank, r.quantity, r.price, r.amount,
        r.cost_per_litre, r.cost_value, r.note, r.issue,
      ]),
      ['Total', '', '', '', '', report.totals?.quantity, '', report.totals?.amount, '', report.totals?.cost_value, '', ''],
      [],
      ['By product', 'Entries', 'Litres', 'Sale reversed', 'Cost value'],
      ...(report.by_product || []).map((g) => [g.name, g.count, g.quantity, g.amount, g.cost_value]),
      [],
      ['By employee', 'Entries', 'Litres', 'Sale reversed'],
      ...(report.by_employee || []).map((g) => [g.name, g.count, g.quantity, g.amount]),
    ];
    const csv = lines.map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `recoup_statement_${report.start_date}_${report.end_date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  };

  const rows = useMemo(() => {
    const all = report?.rows || [];
    return onlyIssues ? all.filter((r) => r.issue) : all;
  }, [report, onlyIssues]);

  const checkList = (field, label, items, placeholder) => (
    <div className="col-md-4">
      <label className="other-report-filter-section-title d-block mb-2">{label}</label>
      <OtherReportSearchableCheckList
        items={items || []}
        selectedIds={inputValues[field] || []}
        onToggle={(id) => toggleSelection(field, id)}
        onChange={(ids) => setSelection(field, ids)}
        searchPlaceholder={placeholder}
        getId={(item) => String(item.id)}
        getLabel={(item) => item.name}
      />
    </div>
  );

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Recoup Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <ReportDateRangeFields
                    startDate={inputValues.start_date}
                    endDate={inputValues.end_date}
                    onChange={({ start_date, end_date }) => setInputValues({ ...inputValues, start_date, end_date })}
                  />
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
              <OtherReportFilterSection title="Filters" description="Optional. Leave empty to show every recoup.">
                <div className="row g-3">
                  {checkList('product', 'Products', meta.products, 'Search products…')}
                  {checkList('employee', 'Employees', meta.employees, 'Search employees…')}
                  {checkList('tank', 'Tanks', meta.tanks, 'Search tanks…')}
                </div>
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating recoup statement" />}
              {report && !loading && (
                <div className="form-wrap balace-sheetwrap">
                  <div className="row mb-3">
                    <div className="col-md">
                      <h2 className="table-title mb-1">{report.pump_name}</h2>
                      <h4 className="table-title mb-1">Recoup Statement</h4>
                      <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                      <p className="mb-2">
                        {report.totals?.count} recoups · {litres(report.totals?.quantity)} L · sale reversed ₹{reportNumberFormat(report.totals?.amount)}
                      </p>
                      <button type="button" className="other-report-btn other-report-btn-secondary" onClick={downloadCsv}>
                        Download Excel (CSV)
                      </button>
                      <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-lg-6">
                      <h5 className="mb-2">By product</h5>
                      <div className="other-report-table-wrap">
                        <table className="other-report-table">
                          <thead><tr><th>Product</th><th>Entries</th><th>Litres</th><th>Sale reversed</th><th>Cost returned to stock</th></tr></thead>
                          <tbody>
                            {(report.by_product || []).map((g) => (
                              <tr key={`p-${g.name}`}>
                                <td>{g.name}</td>
                                <td className="report-amount">{g.count}</td>
                                <td className="report-amount">{litres(g.quantity)}</td>
                                <td className="report-amount">{reportNumberFormat(g.amount)}</td>
                                <td className="report-amount">{reportNumberFormat(g.cost_value)}</td>
                              </tr>
                            ))}
                            <tr className="other-report-totals-row">
                              <td>Total</td>
                              <td className="report-amount">{report.totals?.count}</td>
                              <td className="report-amount">{litres(report.totals?.quantity)}</td>
                              <td className="report-amount">{reportNumberFormat(report.totals?.amount)}</td>
                              <td className="report-amount">{reportNumberFormat(report.totals?.cost_value)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <h5 className="mb-2">By employee</h5>
                      <div className="other-report-table-wrap">
                        <table className="other-report-table">
                          <thead><tr><th>Employee</th><th>Entries</th><th>Litres</th><th>Sale reversed</th></tr></thead>
                          <tbody>
                            {(report.by_employee || []).map((g) => (
                              <tr key={`e-${g.name}`}>
                                <td>{g.name}</td>
                                <td className="report-amount">{g.count}</td>
                                <td className="report-amount">{litres(g.quantity)}</td>
                                <td className="report-amount">{reportNumberFormat(g.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center mt-4 mb-2">
                    <h5 className="mb-0 me-3">Recoup entries</h5>
                    {report.totals?.issues > 0 && (
                      <label className="mb-0">
                        <input type="checkbox" className="me-1" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
                        Show only entries needing a check ({report.totals.issues})
                      </label>
                    )}
                  </div>
                  <div className="other-report-table-wrap">
                    <table className="other-report-table">
                      <thead>
                        <tr>
                          <th>Date</th><th>Employee</th><th>Shift start</th><th>Product</th><th>Tank</th>
                          <th>Litres</th><th>Price/L</th><th>Sale reversed</th><th>Cost/L</th><th>Cost value</th><th>Note</th><th>Check</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.id}>
                            <td>{formatReportDate(r.date)}</td>
                            <td>{r.employee}</td>
                            <td>{r.shift_start || ''}</td>
                            <td>{r.product}</td>
                            <td>{r.tank}</td>
                            <td className="report-amount">{litres(r.quantity)}</td>
                            <td className="report-amount">{reportNumberFormat(r.price)}</td>
                            <td className="report-amount">{reportNumberFormat(r.amount)}</td>
                            <td className="report-amount">{r.cost_per_litre === null ? '' : reportNumberFormat(r.cost_per_litre)}</td>
                            <td className="report-amount">{r.cost_value === null ? '' : reportNumberFormat(r.cost_value)}</td>
                            <td>{r.note}</td>
                            <td style={r.issue ? { color: '#b42318' } : undefined}>{r.issue || ''}</td>
                          </tr>
                        ))}
                        {rows.length === 0 && (
                          <tr><td colSpan={12}>No recoups for this period and filters.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {(report.notes || []).length > 0 && (
                    <div className="mt-3 small text-muted">
                      {report.notes.map((note) => <div key={note}>• {note}</div>)}
                    </div>
                  )}
                  <div className="mt-2 small text-muted">
                    Sale reversed = litres × selling price (debited back to Sales). Cost value = litres × fuel cost returned to tank stock, from the ledger.
                  </div>
                </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default RecoupStatementReport;
